import { Link } from "react-router-dom";
import FallbackImage from "../Common/ImageWrapper";

function BranchList({ branches, isLoading, compact = false }) {
  if (isLoading) {
    return <p className="text-[#525252] text-sm mb-0">Loading branches…</p>;
  }

  if (!Array.isArray(branches) || branches.length === 0) {
    return (
      <p className="text-[#525252] text-xs mb-0">
        Branches will be confirmed by the cooperative after you register.
      </p>
    );
  }

  return (
    <ul className="list-unstyled mb-0 d-flex flex-wrap gap-2">
      {branches.map((branch) => (
        <li
          key={branch.id}
          className="inline-flex items-center gap-1.5 rounded-pill border border-[#e0e0e0] bg-[#fafafa] px-2.5 py-1 text-xs text-[#45464e]"
        >
          <span
            className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--color-secondary,#31778c)]"
            aria-hidden
          />
          <span className="fw-semibold">{branch.name}</span>
        </li>
      ))}
    </ul>
  );
}

export function PublicSignupOrgPanel({
  companyDetails,
  branches,
  isLoadingBranches,
  className = "",
  compact = false,
  showBranches = true,
}) {
  const name = companyDetails?.name || "Your cooperative";
  const address = companyDetails?.address;
  const logo = companyDetails?.logo_url;
  const logoSize = compact ? 64 : 72;

  return (
    <div
      className={`flex flex-col items-center bg-white rounded-xl border border-[#e0e0e0]/80 w-full overflow-visible ${
        compact ? "p-5 max-w-sm" : "p-6 sm:p-7 max-w-md shadow-sm"
      } ${className}`}
    >
      {logo ? (
        <FallbackImage
          src={logo}
          alt={`${name} logo`}
          className="rounded-full object-cover border border-[#e0e0e0]"
          width={logoSize}
          height={logoSize}
          style={{ width: logoSize, height: logoSize }}
        />
      ) : (
        <div
          className="rounded-full bg-primary-soft flex items-center justify-center text-primary1 fw-bold"
          style={{ width: logoSize, height: logoSize, fontSize: compact ? "1.25rem" : "1.5rem" }}
          aria-hidden
        >
          {name.charAt(0)}
        </div>
      )}

      <p
        className={`font-bold text-[#383838] text-center mt-2 mb-0 ${
          compact ? "text-lg leading-snug" : "text-xl"
        }`}
      >
        {name}
      </p>

      {address ? (
        <p className="text-[#525252] text-xs text-center mt-1 mb-3 line-clamp-2">
          {address}
        </p>
      ) : null}

      {showBranches ? (
        <div className="w-full border-t border-[#e0e0e0] pt-3 mt-1">
          <p className="text-xs fw-semibold text-[#383838] mb-1">Active branches</p>
          <BranchList
            branches={branches}
            isLoading={isLoadingBranches}
            compact={compact}
          />
        </div>
      ) : null}
    </div>
  );
}

export function PublicSignupBackLink({ to = "/login", label = "Back to login", className = "" }) {
  return (
    <Link
      to={to}
      className={`inline-flex items-center gap-1 text-sm fw-semibold text-[var(--color-secondary,#31778c)] hover:underline focus:outline-none focus:underline ${className}`}
    >
      <span aria-hidden>←</span> {label}
    </Link>
  );
}

export function PublicSignupStepper({ currentStep }) {
  const steps = ["Personal details", "Payment"];
  return (
    <div className="flex items-center gap-2 mb-4">
      {steps.map((label, index) => {
        const stepNum = index + 1;
        const isActive = currentStep === index;
        const isDone = currentStep > index;
        return (
          <div key={label} className="flex items-center gap-2 flex-1 min-w-0">
            {index > 0 && (
              <div
                className="h-px flex-1 bg-[#e0e0e0]"
                aria-hidden
              />
            )}
            <div
              className={`flex items-center gap-1.5 shrink-0 text-xs sm:text-sm ${
                isActive || isDone ? "text-[#383838] fw-semibold" : "text-[#a1a0a0]"
              }`}
            >
              <span
                className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs ${
                  isDone
                    ? "bg-[#19bc31] text-white"
                    : isActive
                      ? "bg-primary1 text-white"
                      : "bg-[#e0e0e0] text-[#525252]"
                }`}
              >
                {isDone ? "✓" : stepNum}
              </span>
              <span className="truncate">{label}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
